import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  Wheat,
  Scale,
  BookOpen,
  BarChart3,
  CheckSquare,
  Database,
  Users,
  FolderLock,
  LogOut,
  X,
  Shield,
  Bug,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useBugReport } from '@/context/BugReportContext';
import { cn } from '@/lib/utils';

interface MoreMenuDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const MoreMenuDrawer: React.FC<MoreMenuDrawerProps> = ({ isOpen, onClose }) => {
  const { user, profile, role, isAdmin, signOut } = useAuth();
  const { openBugReport } = useBugReport();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs transition-opacity animate-in fade-in"
        onClick={onClose}
      />

      {/* Drawer Panel */}
      <div className="fixed inset-y-0 left-0 w-4/5 max-w-xs bg-slate-900 text-white shadow-2xl flex flex-col z-50 animate-in slide-in-from-left duration-200">
        {/* Header */}
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="text-xl">🐷</span>
            <div>
              <h2 className="text-sm font-bold tracking-tight">VSB Farm Management</h2>
              <p className="text-[10px] text-emerald-400 font-medium uppercase">Mobile Menu</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-slate-400 hover:text-white hover:bg-slate-800"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User Card */}
        <div className="p-4 bg-slate-950/60 border-b border-slate-800 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-700 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
            {profile?.full_name?.charAt(0) || user?.email?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="truncate">
            <p className="text-xs font-semibold truncate text-white">{profile?.full_name || 'User Account'}</p>
            <p className="text-[11px] text-slate-400 truncate">{user?.email}</p>
            <div className="flex items-center gap-1 text-[10px] text-emerald-400 font-bold uppercase mt-0.5">
              <Shield className="w-3 h-3" />
              {role || 'Viewer'}
            </div>
          </div>
        </div>

        {/* Links */}
        <div className="flex-1 overflow-y-auto p-3 space-y-4 no-scrollbar">
          <div>
            <p className="px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
              Operations
            </p>
            <nav className="space-y-1">
              <NavLink
                to="/feed"
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-xs font-medium transition-colors',
                    isActive ? 'bg-emerald-700 text-white font-bold' : 'text-slate-300 hover:bg-slate-800'
                  )
                }
              >
                <Wheat className="w-4 h-4 text-amber-400" />
                <span>Feed Management</span>
              </NavLink>

              <NavLink
                to="/settlements"
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-xs font-medium transition-colors',
                    isActive ? 'bg-emerald-700 text-white font-bold' : 'text-slate-300 hover:bg-slate-800'
                  )
                }
              >
                <Scale className="w-4 h-4 text-sky-400" />
                <span>Grower Settlements</span>
              </NavLink>

              <NavLink
                to="/ledger"
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-xs font-medium transition-colors',
                    isActive ? 'bg-emerald-700 text-white font-bold' : 'text-slate-300 hover:bg-slate-800'
                  )
                }
              >
                <BookOpen className="w-4 h-4 text-purple-400" />
                <span>General Ledger</span>
              </NavLink>

              <NavLink
                to="/reports"
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-xs font-medium transition-colors',
                    isActive ? 'bg-emerald-700 text-white font-bold' : 'text-slate-300 hover:bg-slate-800'
                  )
                }
              >
                <BarChart3 className="w-4 h-4 text-emerald-400" />
                <span>Reports & Analytics</span>
              </NavLink>

              <NavLink
                to="/documents"
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-xs font-medium transition-colors',
                    isActive ? 'bg-emerald-700 text-white font-bold' : 'text-slate-300 hover:bg-slate-800'
                  )
                }
              >
                <FolderLock className="w-4 h-4 text-teal-400" />
                <span>Document Repository</span>
              </NavLink>

              {/* In-App Bug Report Trigger */}
              <button
                type="button"
                onClick={() => {
                  onClose();
                  openBugReport();
                }}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-xs font-medium text-red-300 hover:bg-slate-800 hover:text-red-200 transition-colors text-left"
              >
                <Bug className="w-4 h-4 text-red-400" />
                <span>Report a Bug</span>
              </button>
            </nav>
          </div>

          {isAdmin && (
            <div>
              <p className="px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                Administration
              </p>
              <nav className="space-y-1">
                <NavLink
                  to="/admin/bugs"
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-3 py-2 rounded-md text-xs font-medium transition-colors',
                      isActive ? 'bg-emerald-700 text-white font-bold' : 'text-slate-300 hover:bg-slate-800'
                    )
                  }
                >
                  <Bug className="w-4 h-4 text-red-400" />
                  <span>Bug Reports</span>
                </NavLink>

                <NavLink
                  to="/admin/review-and-resolve"
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-3 py-2 rounded-md text-xs font-medium transition-colors',
                      isActive ? 'bg-emerald-700 text-white font-bold' : 'text-slate-300 hover:bg-slate-800'
                    )
                  }
                >
                  <CheckSquare className="w-4 h-4 text-amber-400" />
                  <span>Review & Resolve</span>
                </NavLink>

                <NavLink
                  to="/admin/master-data"
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-3 py-2 rounded-md text-xs font-medium transition-colors',
                      isActive ? 'bg-emerald-700 text-white font-bold' : 'text-slate-300 hover:bg-slate-800'
                    )
                  }
                >
                  <Database className="w-4 h-4 text-blue-400" />
                  <span>Master Tables</span>
                </NavLink>

                <NavLink
                  to="/admin/users"
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-3 py-2 rounded-md text-xs font-medium transition-colors',
                      isActive ? 'bg-emerald-700 text-white font-bold' : 'text-slate-300 hover:bg-slate-800'
                    )
                  }
                >
                  <Users className="w-4 h-4 text-purple-400" />
                  <span>Users & Security</span>
                </NavLink>
              </nav>
            </div>
          )}
        </div>

        {/* Footer: Sign Out */}
        <div className="p-4 border-t border-slate-800">
          <button
            onClick={() => {
              onClose();
              signOut();
            }}
            className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-md bg-red-950/80 hover:bg-red-900 text-red-200 text-xs font-semibold transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

